import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { ChevronRight, FileText, Package } from 'lucide-react';

import { loadStorefrontView } from './api';
import { Empty, WorkspaceTabs } from './parts';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import {
  CHART, ChartTip, DECIMAL, Flag, HourHeatmap, KpiRow, NUMBER, Panel, PulseMap, Share, averageOf, hourly, pointLabel, tipPoint,
  versusAverage,
} from './MobileParts';
import {
  ChannelMark, Delta, Funnel, MoneyText, ReadState, StorefrontGate, StorefrontHead, WeekdayBars, clock, count, duration,
  kpiDetail, money, moneyOf, otherMoney, previousMoney, primaryMoney, rate, ratio, st, stCount, useStoreNames, useStorefrontSite,
} from './StorefrontParts';

// The store at a glance over a range: what it earned and from how many visits,
// how those visits went from a first page to a paid order, where they came from,
// what they looked at, and when. Every figure is compared with the period before.

export function stageLabel(stage) {
  return st(`stage.${stage}`);
}

// The four figures that matter most, each against the period before.
function Headline({ data }) {
  const { kpis, series } = data;
  const revenue = primaryMoney(kpis.revenue.value);
  const others = otherMoney(kpis.revenue.value);
  return (
    <KpiRow items={[
      {
        label: st('kpi.revenue'),
        value: revenue ? moneyOf(revenue) : st('no_revenue'),
        detail: revenue
          ? kpiDetail(revenue.netMinor, previousMoney(kpis.revenue.previous, revenue.currency), {
            format: (value) => money(value, revenue.currency, { minorUnit: revenue.minorUnit }),
            extra: others.length > 0 ? others.map((entry) => moneyOf(entry)).join(' · ') : null,
          })
          : st('kpi.revenue_none'),
      },
      {
        label: st('kpi.orders'), value: count(kpis.orders.value),
        detail: kpiDetail(kpis.orders.value, kpis.orders.previous),
        trend: series.map((point) => point.orders),
      },
      {
        // Visitors are counted per day, so over a range they are summed, and the label says so.
        label: st('kpi.visitors'), value: count(kpis.visitors.value),
        detail: kpiDetail(kpis.visitors.value, kpis.visitors.previous, {
          extra: kpis.visitors.previous === null ? st('kpi.visitors_summed') : st('kpi.visitors_before', { value: count(kpis.visitors.previous) }),
        }),
        trend: series.map((point) => point.visitors),
      },
      {
        label: st('kpi.sessions'), value: count(kpis.sessions.value),
        detail: kpiDetail(kpis.sessions.value, kpis.sessions.previous),
        trend: series.map((point) => point.sessions),
      },
    ]} />
  );
}

// How the visits behaved: rates and lengths, each with its change.
function Rates({ kpis }) {
  const cells = [
    { key: 'conversion', label: st('kpi.conversion'), value: ratio(kpis.conversionRate.value), delta: kpis.conversionRate },
    { key: 'bag', label: st('kpi.bag_rate'), value: ratio(kpis.bagRate.value), delta: kpis.bagRate },
    { key: 'checkout', label: st('kpi.checkout_rate'), value: ratio(kpis.checkoutRate.value), delta: kpis.checkoutRate },
    { key: 'bounce', label: st('kpi.bounce_rate'), value: ratio(kpis.bounceRate.value), delta: kpis.bounceRate, better: 'down' },
    { key: 'length', label: st('kpi.avg_session'), value: duration(kpis.avgSessionMs.value), delta: kpis.avgSessionMs },
    { key: 'views', label: st('kpi.page_views'), value: count(kpis.pageViews.value), delta: kpis.pageViews },
  ];
  return (
    <dl className="xo-sf-rates">
      {cells.map((cell) => (
        <div key={cell.key}>
          <dt>{cell.label}</dt>
          <dd>
            <strong>{cell.value}</strong>
            <Delta current={cell.delta.value} previous={cell.delta.previous} better={cell.better || 'up'} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

// Traffic (visitors and sessions) or sales (orders and revenue in the main
// currency), point by point over the range.
function Trend({ data }) {
  const [view, setView] = useState('traffic');
  const currency = primaryMoney(data.kpis.revenue.value);
  const points = useMemo(() => data.series.map((point, index) => {
    const revenue = currency && data.revenueSeries[index]
      ? data.revenueSeries[index].revenue.find((entry) => entry.currency === currency.currency) : null;
    return { ...point, revenue: revenue ? revenue.netMinor / (10 ** revenue.minorUnit) : 0, revenueMinor: revenue ? revenue.netMinor : 0 };
  }), [data.series, data.revenueSeries, currency]);
  const average = useMemo(() => averageOf(points.map((point) => (view === 'traffic' ? point.visitors : point.orders))), [points, view]);
  const label = pointLabel(data.step);
  const empty = view === 'traffic' ? points.every((point) => point.sessions === 0) : points.every((point) => point.orders === 0);
  return (
    <>
      <WorkspaceTabs value={view} onChange={setView} label={st('overview.trend_title')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'traffic', label: st('overview.trend_traffic') },
        { value: 'sales', label: st('overview.trend_sales') },
      ]} />
      {empty ? <Empty title={view === 'traffic' ? st('overview.no_traffic') : st('overview.no_sales')} /> : (
        <div className="xo-mob-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={CHART.grid} />
              <XAxis dataKey="at" tickFormatter={label} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
              <YAxis yAxisId="count" allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={36} />
              {view === 'sales' && currency && (
                <YAxis yAxisId="money" orientation="right" tickLine={false} axisLine={false} tick={CHART.tick} width={54}
                  tickFormatter={(value) => money(value * (10 ** currency.minorUnit), currency.currency, { compact: true, minorUnit: currency.minorUnit })} />
              )}
              <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                <ChartTip title={tipPoint(data.step)}
                  rows={(point) => (view === 'traffic' ? [
                    { name: st('kpi.visitors'), color: CHART.ink, mark: 'line', text: NUMBER.format(point.visitors) },
                    { name: st('kpi.sessions'), color: CHART.pale, mark: 'bar', text: NUMBER.format(point.sessions) },
                    { name: st('kpi.page_views'), color: CHART.soft, mark: 'bar', text: NUMBER.format(point.pageViews) },
                  ] : [
                    { name: st('kpi.orders'), color: CHART.pale, mark: 'bar', text: NUMBER.format(point.orders) },
                    currency ? { name: st('kpi.revenue'), color: CHART.ink, mark: 'line', text: money(point.revenueMinor, currency.currency, { minorUnit: currency.minorUnit }) } : null,
                  ])}
                  facts={(point) => (view === 'traffic' ? [
                    point.sessions > 0 ? { label: st('overview.tip_bag_adds'), value: NUMBER.format(point.bagAdds) } : null,
                    versusAverage(point.visitors, average),
                  ] : [
                    point.sessions > 0 ? { label: st('overview.tip_conversion'), value: rate(point.orders, point.sessions) } : null,
                    versusAverage(point.orders, average),
                  ])} />
              )} />
              {view === 'traffic' ? (
                <>
                  <Bar yAxisId="count" dataKey="sessions" fill={CHART.pale} radius={[3, 3, 0, 0]} maxBarSize={22} name={st('kpi.sessions')} />
                  <Line yAxisId="count" type="monotone" dataKey="visitors" stroke={CHART.ink} strokeWidth={2.2} dot={false} name={st('kpi.visitors')} />
                </>
              ) : (
                <>
                  <Bar yAxisId="count" dataKey="orders" fill={CHART.pale} radius={[3, 3, 0, 0]} maxBarSize={22} name={st('kpi.orders')} />
                  {currency && <Line yAxisId="money" type="monotone" dataKey="revenue" stroke={CHART.ink} strokeWidth={2.2} dot={false} name={st('kpi.revenue')} />}
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="xo-mob-key">
            {view === 'traffic' ? (
              <>
                <span><i className="is-line" />{st('kpi.visitors')}</span>
                <span><i style={{ background: CHART.pale }} />{st('kpi.sessions')}</span>
              </>
            ) : (
              <>
                <span><i style={{ background: CHART.pale }} />{st('kpi.orders')}</span>
                {currency && <span><i className="is-line" />{st('overview.revenue_in', { currency: currency.currency })}</span>}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Where the sessions came from, and what each channel brought in.
export function ChannelRows({ channels }) {
  if (channels.length === 0) return <Empty title={st('overview.no_channels')} />;
  const total = channels.reduce((sum, row) => sum + row.sessions, 0);
  return (
    <ol className="xo-sf-channels">
      {channels.map((row) => (
        <li key={row.channel}>
          <ChannelMark channel={row.channel} />
          <Share value={row.sessions} total={total} />
          <span className="xo-sf-channels-count"><strong>{NUMBER.format(row.sessions)}</strong><small>{rate(row.sessions, total)}</small></span>
          <span className="xo-sf-channels-money"><MoneyText list={row.revenue} compact /><small>{stCount('orders_count', row.orders)}</small></span>
        </li>
      ))}
    </ol>
  );
}

function TopPages({ pages }) {
  const names = useStoreNames();
  if (pages.length === 0) return <Empty title={st('overview.no_pages')} />;
  const most = Math.max(1, ...pages.map((page) => page.views));
  return (
    <ol className="xo-mob-rank xo-sf-rank">
      {pages.slice(0, 8).map((page) => (
        <li key={page.path}>
          <span className="xo-mob-rank-icon"><FileText aria-hidden="true" /></span>
          <span className="xo-mob-rank-main">
            <Link className="xo-sf-row-link" to={`/xomics/store/pages?path=${encodeURIComponent(page.path)}`} title={page.path}>
              <strong>{names.pageOrPath(page.path)}</strong>
            </Link>
            <small><code className="xo-sf-path-small">{page.path}</code> · {stCount('daily_visitors_count', page.visitors)}</small>
          </span>
          <Share value={page.views} total={most} />
          <em>{NUMBER.format(page.views)}</em>
        </li>
      ))}
    </ol>
  );
}

function TopProducts({ products }) {
  if (products.length === 0) return <Empty title={st('overview.no_products')} />;
  return (
    <ol className="xo-sf-products">
      {products.map((product) => (
        <li key={product.productCode}>
          <span className="xo-mob-rank-icon"><Package aria-hidden="true" /></span>
          <span className="xo-mob-rank-main">
            <strong title={product.name || product.productCode}>{product.name || product.productCode}</strong>
            <small>{st('overview.bag_rate', { rate: rate(product.bagAdds, product.views) })}</small>
          </span>
          <span className="xo-sf-mini-funnel" aria-label={st('overview.product_steps', { views: product.views, bags: product.bagAdds, bought: product.purchases })}>
            <span><b>{NUMBER.format(product.views)}</b><small>{st('overview.views')}</small></span>
            <span><b>{NUMBER.format(product.bagAdds)}</b><small>{st('overview.bag_adds')}</small></span>
            <span><b>{NUMBER.format(product.purchases)}</b><small>{st('overview.bought')}</small></span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Countries({ countries }) {
  if (countries.length === 0) return <Empty title={st('overview.no_countries')} />;
  const most = Math.max(1, ...countries.map((country) => country.visitors));
  return (
    <div className="xo-sf-geo">
      <PulseMap countries={countries} valueKey="visitors" valueLabel={st('kpi.visitors')} height={250} label={st('overview.map_label')} />
      <ol className="xo-mob-places">
        {countries.slice(0, 6).map((country) => (
          <li key={country.code}>
            <Flag code={country.code} />
            <span><strong>{countryName(country.code)}</strong><small>{stCount('sessions_count', country.sessions)}</small></span>
            <Share value={country.visitors} total={most} />
            <em>{stCount('daily_visitors_count', country.visitors)}</em>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Report({ site, days, setDays, store }) {
  const load = useCallback(() => loadStorefrontView('overview', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 60000 });
  const head = <StorefrontHead title={st('overview.title')} subtitle={st('overview.subtitle', { site: site.name })} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('overview.loading')} /></div>;
  const data = report.data;
  const funnel = data.funnel.map((step) => ({ key: step.stage, label: stageLabel(step.stage), count: step.sessions }));
  const sessionsPerPoint = data.series.length > 0 ? data.kpis.sessions.value / data.series.length : 0;
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <Headline data={data} />
      <Rates kpis={data.kpis} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={st('overview.trend_title')}
          detail={st(hourly(data.step) ? 'overview.trend_detail_hours' : 'overview.trend_detail', { average: DECIMAL.format(sessionsPerPoint) })}>
          <Trend data={data} />
        </Panel>
        <Panel title={st('overview.channels_title')} detail={st('overview.channels_detail')}
          aside={<Link className="xo-mob-link" to="/xomics/store/acquisition">{st('overview.all_sources')}<ChevronRight aria-hidden="true" /></Link>}>
          <ChannelRows channels={data.channels} />
        </Panel>
      </div>

      <Panel title={st('overview.funnel_title')} detail={st('overview.funnel_detail')}
        aside={<Link className="xo-mob-link" to="/xomics/store/checkout-funnel">{st('overview.checkout_link')}<ChevronRight aria-hidden="true" /></Link>}>
        <Funnel steps={funnel} />
      </Panel>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={st('overview.pages_title')} detail={st('overview.pages_detail')}
          aside={<Link className="xo-mob-link" to="/xomics/store/pages">{st('overview.all_pages')}<ChevronRight aria-hidden="true" /></Link>}>
          <TopPages pages={data.topPages} />
        </Panel>
        <Panel title={st('overview.products_title')} detail={st('overview.products_detail')}
          aside={<Link className="xo-mob-link" to="/xomics/store/product-funnel">{st('overview.all_products')}<ChevronRight aria-hidden="true" /></Link>}>
          <TopProducts products={data.topProducts} />
        </Panel>
      </div>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={st('overview.countries_title')} detail={st('overview.countries_plain')}>
          <Countries countries={data.countries} />
        </Panel>
        <Panel title={st('overview.when_title')} detail={st('overview.when_detail')}>
          <HourHeatmap grid={data.heatmap} countKey="ui.storefront.sessions_count" />
          <WeekdayBars grid={data.heatmap} />
        </Panel>
      </div>
      <p className="xo-mob-footnote">{st('overview.footnote', { when: clock(data.generatedAt) })}</p>
    </div>
  );
}

export default function StorefrontOverview() {
  const [days, setDays] = useState(30);
  const store = useStorefrontSite();
  return (
    <StorefrontGate store={store}>
      {(site) => <Report site={site} days={days} setDays={setDays} store={store} />}
    </StorefrontGate>
  );
}
